import type { Translation } from "./types";

const en: Translation = {
    errors: {
        network: "Network error, please try again",
        tooLarge: "The files are too large to upload, please try smaller ones",
        unknown: "Something went wrong",
    },
    login: {
        title: "Admins Dashboard",
        subtitle: "Enter your phone number to access the system",
        phonePlaceholder: "xxx xxx xxx",
        submitButton: "Login",
    },
    verifyOtp: {
        title: "OTP Verification",
        subtitle: "Enter the 4-digit verification code sent to your phone",
        resendLink: "Resend code",
        resendHint: "Didn't receive a code?",
        submitButton: "Verify",
        reEnterPhone: "Re-enter phone number",
    },
    contacts: {
        title: "Contact Requests",
        search: "Search by name, email, phone or message",
        tabAll: "All",
        tabNew: "New",
        statTotal: "Total",
        statNew: "New",
        statRead: "Read",
        statReplied: "Replied",
        deleteWarning: "This permanently deletes the selected contact requests",
        noContact: "—",
        colEmail: "Email",
        colPhone: "Phone",
        view: "View message",
        repeats: "{count}×",
        repeatsHint: "Show every request from this contact",
        filteredBy: "All requests from",
        clearChannel: "Clear this filter",
        copy: "Copy",
        copied: "Copied",
        colMessage: "Message",
    },
    sideBar: {
        contacts: "Contacts",
        home: "Home",
        stores: "Stores",
        clients: "Clients",
        coupons: "Coupons",
        subscriptions: "Subscriptions",
        support: "Support",
        logs: "Logs",
    },
    bottomBar: {
        more: "More",
        close: "Close",
    },
    profilePopup: {
        superAdmin: "Super admin",
        admin: "Admin",
        logout: "Log out",
        logoutConfirmMessage: "Are you sure you want to log out?",
        logoutConfirmAction: "Log out",
        logoutCancel: "Cancel",
    },
    home: {
        title: "Home",
        greetingMorning: "Good morning",
        greetingAfternoon: "Good afternoon",
        greetingEvening: "Good evening",
        subtitle: "Here is where Menuket stands",
        error: "Could not load the overview",
        retry: "Try again",
        rangeToday: "Today",
        range7d: "Last 7 days",
        range30d: "Last 30 days",
        range90d: "Last 90 days",
        range12m: "Last 12 months",
        rangeAll: "All time",
        rangeCustom: "Custom",
        periodAll: "All plans",
        attentionTitle: "Needs you",
        attentionClear: "Nothing waiting on you",
        attentionTickets: "Open tickets",
        attentionPayments: "Payments to confirm",
        attentionErrors: "Server errors",
        attentionPaused: "Paused stores",
        storesTitle: "Stores",
        storesTotal: "Total stores",
        storesNew: "New stores",
        storesSubscribed: "Subscribed",
        storesSubscribedHint: "Holding a valid plan right now",
        storesActive: "Active stores",
        storesPaused: "Paused",
        storesNeverPaid: "Never subscribed",
        storesGrowth: "New stores over time",
        storesByType: "By type",
        storesTopActive: "Busiest stores",
        moneyTitle: "Money",
        revenue: "Earned",
        revenueHint: "After coupon discounts",
        gross: "At list price",
        grossHint: "Before any discount",
        discountGiven: "Discounts given",
        discountGivenHint: "Taken off by coupons",
        avgTicket: "Average sale",
        pendingMoney: "Awaiting payment",
        pendingMoneyHint: "Requested, not yet transferred",
        subsPaid: "Paid subscriptions",
        subsPaidHint: "Plans and branch add-ons",
        revenueOverTime: "Revenue over time",
        subsByStatus: "Requests by status",
        subsByPeriod: "Revenue by plan",
        seriesRevenue: "Earned",
        seriesDiscount: "Discount",
        couponsTitle: "Coupons",
        couponsTotal: "Total coupons",
        couponsActive: "Live",
        couponsRedeemed: "Redemptions",
        couponsDiscounted: "Money given away",
        supportTitle: "Support",
        ticketsOpen: "Open tickets",
        ticketsOpenHint: "Right now, whenever they were raised",
        ticketsPending: "Untouched",
        ticketsContacted: "In progress",
        ticketsTotal: "Tickets raised",
        ticketsLatest: "Waiting longest",
        ticketsNone: "No unanswered tickets",
        logsTitle: "System health",
        logsTotal: "Requests",
        logsErrorRate: "Error rate",
        logsErrors: "Errors",
        logsRejected: "Refused",
        logsAvgResponse: "Average response",
        logsTraffic: "Traffic",
        logsProblems: "Failures",
        logsCapped: "Logs are kept for a shorter window than the one selected",
        egp: "EGP",
        viewAll: "View all",
        activityHint: "Used the dashboard in this period",
        noData: "Nothing in this period",
    },
    table: {
        retry: "Try again",
        empty: "Nothing here yet",
        emptyHint: "Try a different search or filter",
        selectAll: "Select all rows",
        selectRow: "Select row",
        actions: "Actions",
        previous: "Previous",
        next: "Next",
        back: "Back",
        filtersOpen: "Filters",
        filtersClose: "Close filters",
        filtersClear: "Clear",
        bulkTitle: "Bulk actions",
        bulkSelected: "selected",
        bulkApply: "Apply",
        bulkDelete: "Delete",
        bulkClear: "Clear selection",
        bulkOn: "Yes",
        bulkOff: "No",
        preview: "Open",
        deleteRow: "Delete",
        deleteTitle: "This cannot be undone. Delete anyway?",
        deleteCancel: "Cancel",
        deleteConfirm: "Delete permanently",
        countOf: "of",
        any: "Any",
    },
    cols: {
        name: "Name",
        store: "Store",
        client: "Client",
        status: "Status",
        period: "Plan",
        price: "Price",
        paid: "Paid",
        discount: "Discount",
        code: "Code",
        title: "Title",
        message: "Message",
        phone: "Phone",
        gender: "Gender",
        created: "Created",
        updated: "Updated",
        endDate: "Ends",
        dueDate: "Payment due",
        expiry: "Expires",
        uses: "Used",
        maxUses: "Max uses",
        active: "Active",
        owned: "Owns",
        access: "Access",
        method: "Method",
        route: "Route",
        severity: "Severity",
        outcome: "Outcome",
        responseTime: "Time",
        surface: "Surface",
        model: "Section",
        unlimited: "Unlimited",
        never: "Never",
    },
    lists: {
        subscriptions: {
            statTotal: "Total subscriptions",
            statActive: "Active",
            statPending: "Awaiting payment",
            statRevenue: "Collected",
            tabAll: "All",
            tabPending: "Awaiting payment",
            tabActive: "Active",
            search: "Search by store name or slug",
            deleteWarning: "This deletes the subscription row and releases any coupon use it was holding",
        },
        support: {
            statTotal: "Total tickets",
            statPending: "Untouched",
            statContacted: "In progress",
            statSolved: "Solved",
            tabAll: "All",
            tabOpen: "Open",
            tabSolved: "Solved",
            search: "Search by title or message",
            deleteWarning: "This deletes the ticket for everybody, not just the client",
        },
        coupons: {
            statTotal: "Total coupons",
            statActive: "Live",
            statRedeemed: "Redemptions",
            statDiscounted: "Given away",
            tabAll: "All",
            tabActive: "Live",
            tabInactive: "Disabled",
            search: "Search by code or title",
            deleteWarning: "Codes that were already spent are kept — only unused ones are deleted",
        },
        clients: {
            statTotal: "Total clients",
            statOwners: "Owners",
            tabAll: "All",
            search: "Search by name or phone",
            deleteWarning: "This deletes the account and its tickets. A client who still owns a store is skipped",
        },
        logs: {
            statTotal: "Requests",
            statErrors: "Errors",
            statRejected: "Refused",
            statAvg: "Average response",
            tabAll: "All",
            tabErrors: "Errors",
            search: "Search by route, URL or message",
            deleteWarning: "Logs expire on their own — deleting only clears them sooner",
        },
    },
    enums: {
        contactStatus: { new: "New", read: "Read", replied: "Replied" },
        storeType: {
            cafe: "Cafe",
            restaurant: "Restaurant",
            both: "Cafe & Restaurant",
            other: "Other",
        },
        subscriptionStatus: {
            pending: "Pending",
            active: "Active",
            coming: "Coming",
            grace: "Grace period",
            paused: "Paused",
            canceled: "Canceled",
            ended: "Ended",
        },
        subscriptionPeriod: {
            monthly: "Monthly",
            quarterly: "Quarterly",
            annually: "Annually",
        },
        logOutcome: {
            success: "Success",
            failed: "Failed",
            unauthorized: "Unauthorized",
            forbidden: "Forbidden",
            notFound: "Not found",
            serverError: "Server error",
        },
        logSeverity: {
            success: "Success",
            rejected: "Rejected",
            error: "Error",
        },
        logSurface: {
            clients: "Clients app",
            admins: "Admins app",
            menu: "Menu",
            website: "Website",
            webhooks: "Webhooks",
            other: "Other",
        },
        logResponseBucket: {
            fast: "Fast (under 300ms)",
            normal: "Normal (300ms - 1s)",
            slow: "Slow (1 - 3s)",
            critical: "Very slow (over 3s)",
        },
    },
    stores: {
        colName: "Store",
        colSlug: "Slug",
        colType: "Type",
        colSubscription: "Subscription",
        colBranches: "Branches",
        colProducts: "Products",
        colCreated: "Joined",
        tabAll: "All",
        tabSubscribed: "Subscribed",
        tabLapsed: "Lapsed",
        statTotal: "Total stores",
        statSubscribed: "Subscribed",
        statPaused: "Paused",
        statPending: "Awaiting payment",
        statNoSub: "No subscription",
        deleteWarning: "This deletes the store and every branch, product, subscription and ticket belonging to it",
        searchPlaceholder: "Search by name or slug",
        filterAll: "All",
        filterNone: "No subscription",
        empty: "No stores found",
        emptyHint: "Try a different search or filter",
        retry: "Try again",
        branchesCount: "Branches",
        productsCount: "Products",
        noSubscription: "No subscription",
        results: "stores",
        previous: "Previous",
        next: "Next",
        page: "Page",
    },
    storeDetail: {
        back: "Back to stores",
        openMenu: "Open menu",
        copyLink: "Copy link",
        copied: "Copied",
        notFound: "Store not found",
        retry: "Try again",
        createdAt: "Joined",
        sectionMenu: "Menu",
        sectionBranches: "Branches",
        sectionClients: "Clients",
        sectionSubscription: "Subscription",
        sectionSupport: "Support",
        menuProducts: "Products",
        menuActive: "Active",
        menuHidden: "Hidden",
        menuDiscounted: "Discounted",
        menuUnpriced: "Unpriced",
        menuCategories: "Categories",
        branchesEmpty: "This store has no branches",
        branchInactive: "inactive",
        branchHidden: "hidden",
        branchPrices: "price overrides",
        clientsEmpty: "This store has no clients",
        owner: "Owner",
        staff: "Staff",
        allBranches: "All branches",
        subNone: "This store has no live subscription",
        subPeriod: "Period",
        subPrice: "Price",
        subBranches: "Branch slots",
        subStartDate: "Starts",
        subEndDate: "Ends",
        subDaysLeft: "Days left",
        subPaymentDue: "Payment due",
        subCoupon: "Coupon",
        subHistory: "History",
        subHistoryEmpty: "No earlier subscriptions",
        supportPending: "Pending",
        supportContacted: "Contacted",
        supportSolved: "Solved",
        days: "days",
        currency: "EGP",
        sectionActivity: "Activity & failures",
        activityRequests: "Total requests",
        activityErrors: "Errors",
        activityRejected: "Rejected requests",
        activityErrorRate: "Error rate",
        activityAvgResponse: "Avg response time",
        activityPerDay: "Requests per day",
        activityTopFailing: "Most frequent errors",
        activityBySurface: "By surface",
        activityEmpty: "No activity recorded for this store in this period",
        activityRange: "Last {days} days",
        activityOpenLogs: "View all logs",
        activityLastSeen: "Last seen",
    },
    logs: {
        searchPlaceholder: "Search by route or message",
        empty: "No logs found",
        emptyHint: "Try widening the time range or removing some filters",
        filters: "Filters",
        clearFilters: "Clear filters",
        apply: "Apply",
        groupByHour: "By hour",
        groupByDay: "By day",
        totalRequests: "Total requests",
        errors: "Errors",
        rejected: "Rejected",
        errorRate: "Error rate",
        avgResponse: "Avg response",
        noErrors: "No errors in this period 🎉",
        overTime: "Requests over time",
        bySeverity: "By severity",
        topFailing: "Top failing routes",
        slowest: "Slowest routes",
        topFailingStores: "Most affected stores",
        filterMethod: "Method",
        filterSurface: "Surface",
        filterModel: "Section",
        filterStatus: "Status code",
        filterOutcome: "Outcome",
        filterLang: "Language",
        filterResponseTime: "Response time",
        filterFrom: "From",
        filterTo: "To",
        any: "Any",
        detailBack: "Back to logs",
        detailTitle: "Log details",
        detailNotFound: "Log not found",
        detailRequest: "Request",
        detailQuery: "Query",
        detailBody: "Body",
        detailResponse: "Response",
        detailNoPayload: "No payload stored",
        detailTruncated: "Part of the payload was cut because of its size",
        detailStore: "Store",
        detailBranch: "Branch",
        detailClient: "Client",
        detailAdmin: "Admin",
        detailIp: "IP address",
        detailUserAgent: "User agent",
        detailLang: "Language",
        detailResponseTime: "Response time",
        detailAt: "Time",
        detailMessage: "Message",
        retentionNote: "Logs are kept for 10 days, then deleted automatically",
    },
    subscriptions: {
        tabPlans: "Plans",
        tabAddons: "Branch add-ons",
        searchPlaceholder: "Search by store name or slug",
        filterAll: "All",
        empty: "Nothing here",
        emptyHint: "Try a different filter",
        retry: "Try again",
        requested: "Requested",
        paymentDue: "Payment due",
        starts: "Starts",
        ends: "Ends",
        slots: "Branch slots",
        coupon: "Coupon",
        was: "was",
        activate: "Review & activate",
        cancel: "Cancel request",
        restore: "Restore to pending",
        addonBranches: "extra branches",
        addonActivate: "Review & activate",
        addonFor: "on top of",
        addonRequested: "Requested",
        addonActivated: "Activated",
        addonFilterPending: "Pending",
        addonFilterActivated: "Activated",
        addonFilterRejected: "Rejected",
        confirmCancelMessage: "Cancel this request? The coupon use, if any, is handed back.",
        confirmCancelAction: "Cancel request",
        confirmRestoreMessage: "Restore this request to pending? The coupon use is taken again.",
        confirmRestoreAction: "Restore",
        keep: "Keep it",
    },
    activatePopup: {
        title: "Confirm the payment",
        expected: "Expected",
        amountLabel: "Amount received",
        mismatch: "This does not match the expected amount",
        confirm: "Activate",
        cancel: "Cancel",
        queuedWarning: "This store already holds an active plan, so activating this row queues it as coming rather than starting it now.",
    },
    support: {
        searchPlaceholder: "Search tickets",
        filterOpen: "Open",
        filterAll: "All",
        filterPending: "Pending",
        filterContacted: "Contacted",
        filterSolved: "Solved",
        filterDeleted: "Deleted",
        empty: "No tickets here",
        emptyHint: "Try a different filter",
        markPending: "Mark pending",
        markContacted: "Mark contacted",
        markSolved: "Mark solved",
        openedBy: "Opened by",
        at: "at",
    },
    coupons: {
        searchPlaceholder: "Search by code or title",
        filterAll: "All",
        filterActive: "Active",
        filterInactive: "Inactive",
        empty: "No coupons yet",
        emptyHint: "Create one to get started",
        create: "New coupon",
        edit: "Edit",
        toggleOn: "Enable",
        toggleOff: "Disable",
        delete: "Delete",
        usage: "Usage",
        redeemed: "Redeemed",
        discounted: "Discount given",
        uses: "uses",
        unlimited: "unlimited",
        expires: "Expires",
        neverExpires: "No expiry",
        reusable: "Reusable per store",
        oncePerStore: "Once per store",
        off: "off",
        confirmDeleteMessage: "Delete this coupon? This cannot be undone.",
        confirmDeleteAction: "Delete",
        keep: "Keep it",
        formTitle: "Title",
        formCode: "Code",
        formDiscountType: "Discount type",
        formPercentage: "Percentage",
        formFixed: "Fixed",
        formDiscountAmount: "Discount value",
        formTypes: "Valid on",
        formPeriods: "Covers plans",
        formMaxUses: "Max uses (blank = unlimited)",
        formExpiresAt: "Expires on (blank = never)",
        formAllowReuse: "Same store may redeem it again",
        formIsActive: "Active",
        formSave: "Save",
        formCancel: "Cancel",
        actionNew: "New plan",
        actionRenew: "Renewal",
        actionBranch: "Branch add-on",
        usageEmpty: "This coupon has not been used yet",
        usagePaid: "Paid",
        usageWaiting: "Reserved",
        usageReleased: "Released",
        usageKindSubscription: "Plan",
        usageKindAddon: "Branch add-on",
    },
    clients: {
        searchPlaceholder: "Search by name or phone",
        empty: "No clients found",
        emptyHint: "Try a different search",
        owns: "Owns",
        worksAt: "Works at",
        stores: "stores",
        back: "Back to clients",
        notFound: "Client not found",
        ownedStores: "Owned stores",
        accessStores: "Granted access",
        noStores: "No stores",
        supportTickets: "Support tickets",
        joined: "Joined",
        edit: "Edit profile",
        formName: "Name",
        formGender: "Gender",
        genderMale: "Male",
        genderFemale: "Female",
        genderUnset: "Not set",
        formSave: "Save",
        formCancel: "Cancel",
        allBranches: "All branches",
    },
    notFound: {
        title: "Page not found",
        subtitle: "The page you are looking for does not exist",
        backButton: "Back to home",
    },
};

export default en;
